import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useEffect, useState } from 'react';
import { DATASET_SHAPES } from '../datasets/datasetTypes';
import { ANALYTICS_GRAPH_TYPES } from './graphCatalog';

const normalizeComparison = (dataset) => {
  if (!dataset) return { metrics: [], categories: [] };
  if (dataset.shape === DATASET_SHAPES.CATEGORICAL_COMPARISON) {
    return dataset;
  }
  if (dataset.shape === DATASET_SHAPES.GOAL_PROGRESS) {
    return {
      ...dataset,
      metrics: [
        { id: 'actual', label: 'Actual', unit: 'count' },
        { id: 'pipeline', label: 'Pipeline', unit: 'count' },
        { id: 'target', label: 'Target', unit: 'count' },
        { id: 'percentComplete', label: 'Complete', unit: '%' },
        { id: 'daysRemaining', label: 'Days remaining', unit: 'days' }
      ],
      categories: dataset.items.map((item) => ({
        id: item.id,
        label: item.label,
        tone: item.tone || 'accent',
        metrics: {
          actual: item.actual,
          pipeline: item.pipeline,
          target: item.target,
          percentComplete: item.percentComplete,
          daysRemaining: item.daysRemaining
        }
      }))
    };
  }
  return { metrics: [], categories: [] };
};

const formatMetric = (value, unit) => {
  if (unit === '$') return `$${Number(value).toLocaleString()}`;
  if (unit === '%') return `${Number(value).toFixed(1)}%`;
  if (unit === 'days' || unit === 'أيام') return `${Number(value).toFixed(1)} ${unit}`;
  return Number(value).toLocaleString();
};

const formatTableValue = (value, column) => {
  if (!column) return `${value ?? ''}`;
  if (column.format === 'percent') return `${Number(value || 0).toFixed(1)}%`;
  if (column.format === 'days') return `${Number(value || 0).toFixed(1)} ${column.unit || 'days'}`;
  if (column.format === 'score') return Number(value || 0).toFixed(1);
  return formatMetric(value, column.unit);
};

const getRiskLabel = (tone) => {
  if (tone === 'success') return uiText('Healthy');
  if (tone === 'warning') return uiText('Watch');
  if (tone === 'danger') return uiText('At risk');
  return uiText('Active');
};

const averageMetric = (categories = [], metricId) =>
  categories.length ? categories.reduce((sum, category) => sum + (Number(category.metrics[metricId]) || 0), 0) / categories.length : 0;

const METRIC_HELP = {
  pipelineDepth: 'Active ATS candidates currently in this role, excluding hired or accepted candidates and known rejected, withdrawn, or declined candidates.'
};

const CATEGORY_TONE_SEQUENCE = ['team-blue', 'team-green', 'team-orange', 'team-teal', 'team-magenta'];

const wrapSvgText = (text = '', maxLineLength = 46) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxLineLength && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) lines.push(current);
  return lines;
};

const ComparisonVisual = ({ model }) => {
  useUiLanguage();
  const rootDataset = model.dataset;
  const [selectedViewId, setSelectedViewId] = useState(rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '');
  useEffect(() => {
    const nextDefault = rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '';
    if (!nextDefault) return;
    setSelectedViewId((currentId) => {
      if (!rootDataset?.views?.[currentId] || currentId !== nextDefault) {
        return nextDefault;
      }
      return currentId;
    });
  }, [rootDataset]);
  const activeDataset = rootDataset?.views?.[selectedViewId] || rootDataset;
  const dataset = normalizeComparison(activeDataset);
  const metrics = dataset.metrics || [];
  const graphConfig = model.meta?.graphConfig?.[model.activeGraph] || {};
  const rawCategories = dataset.categories || [];
  const categories = graphConfig.distinctCategoryTones
    ? rawCategories.map((category, index) => ({
        ...category,
        seriesTone: category.seriesTone || CATEGORY_TONE_SEQUENCE[index % CATEGORY_TONE_SEQUENCE.length]
      }))
    : rawCategories;
  const metricMap = Object.fromEntries(metrics.map((metric) => [metric.id, metric]));
  const primary = metricMap[graphConfig.primaryMetricId] || metrics[0];
  const secondary = metricMap[graphConfig.secondaryMetricId] || metrics[1];
  const tertiary = metricMap[graphConfig.tertiaryMetricId] || metrics[2];
  const displayMetric = metricMap[graphConfig.displayMetricId] || primary;
  const barMetricIds = graphConfig.barMetricIds || [primary?.id, secondary?.id, tertiary?.id].filter(Boolean);
  const contextMetricIds = graphConfig.contextMetricIds || [secondary?.id, tertiary?.id].filter(Boolean);
  const onSelectCategory = model.options?.onSelectCategory;

  if (!primary || !categories.length) return null;

  const secondaryMax = secondary ? Math.max(...categories.map((category) => Number(category.metrics[secondary.id]) || 0), 1) : 1;

  if (model.activeGraph === ANALYTICS_GRAPH_TYPES.RADIAL_RINGS) {
    return (
      <div className="analytics-visual analytics-visual-radials">
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
            {rootDataset.selectors.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                onClick={() => setSelectedViewId(option.id)}
              >
                {uiText(option.label)}
              </button>
            ))}
          </div>
        ) : null}
        {categories.map((category) => {
          const value = Number(category.metrics.percentComplete ?? category.metrics[primary.id]) || 0;
          const dashOffset = 251.2 - ((Math.max(0, Math.min(value, 100)) / 100) * 251.2);
          return (
            <div key={category.id} className="analytics-visual-radial">
              <svg viewBox="0 0 96 96" className="analytics-visual-radial-svg" role="img" aria-label={uiText(category.label)}>
                <circle cx="48" cy="48" r="40" className="analytics-visual-radial-base" />
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  className={`analytics-visual-radial-progress ${category.tone || 'accent'}`}
                  strokeDasharray="251.2"
                  strokeDashoffset={dashOffset}
                />
              </svg>
              <strong className="analytics-visual-number">{formatMetric(value, '%')}</strong>
              <span className="analytics-visual-label">{uiText(category.label)}</span>
            </div>
          );
        })}
      </div>
    );
  }

  if (model.activeGraph === ANALYTICS_GRAPH_TYPES.SCATTER || model.activeGraph === ANALYTICS_GRAPH_TYPES.BUBBLE_CHART) {
    const xMetric = dataset.shape === DATASET_SHAPES.GOAL_PROGRESS
      ? 'percentComplete'
      : graphConfig.xMetricId || primary.id;
    const yMetric = dataset.shape === DATASET_SHAPES.GOAL_PROGRESS
      ? 'daysRemaining'
      : graphConfig.yMetricId || secondary?.id || primary.id;
    const bubbleMetric = dataset.shape === DATASET_SHAPES.GOAL_PROGRESS
      ? 'pipeline'
      : graphConfig.bubbleMetricId || tertiary?.id || primary.id;
    const isCompactScatter = model.size === 'compact';
    const isLargeScatter = model.size === 'large';
    const isMediumScatter = model.size === 'medium' || model.size === 'three-quarter';
    const xMax = Math.max(...categories.map((category) => Number(category.metrics[xMetric]) || 0), 1);
    const yMax = Math.max(...categories.map((category) => Number(category.metrics[yMetric]) || 0), 1);
    const bubbleMax = Math.max(...categories.map((category) => Number(category.metrics[bubbleMetric]) || 0), 1);
    const xAverage = averageMetric(categories, xMetric);
    const yAverage = averageMetric(categories, yMetric);
    const chartWidth = isLargeScatter ? 920 : isMediumScatter ? 760 : 540;
    const chartHeight = isLargeScatter ? 520 : isMediumScatter ? 420 : 300;
    const plotLeft = isLargeScatter ? 84 : isMediumScatter ? 76 : 56;
    const plotRight = isLargeScatter ? 780 : isMediumScatter ? 640 : 460;
    const plotTop = isLargeScatter ? 72 : isMediumScatter ? 66 : 40;
    const plotBottom = isLargeScatter ? 410 : isMediumScatter ? 332 : 226;
    const plotWidth = plotRight - plotLeft;
    const plotHeight = plotBottom - plotTop;
    const useCenteredQuadrants = graphConfig.presentation === 'quadrant_scatter';
    const xMidValue = useCenteredQuadrants ? xMax / 2 : xAverage;
    const yMidValue = useCenteredQuadrants ? yMax / 2 : yAverage;
    const xAveragePosition = plotLeft + ((xMidValue / xMax) * plotWidth);
    const yAveragePosition = plotBottom - ((yMidValue / yMax) * plotHeight);
    const quadrantLabels = graphConfig.quadrantLabels || {};
    const showQuadrants = graphConfig.presentation === 'quadrant_scatter';
    const showQuadrantLabels = Object.values(quadrantLabels).some(Boolean);
    const rankedCategories = [...categories].sort(
      (left, right) => (Number(right.metrics[yMetric]) || 0) - (Number(left.metrics[yMetric]) || 0)
    );
    const yAxisLabel = metricMap[yMetric]?.label || yMetric;
    const activeMetricHelp = METRIC_HELP[yMetric] ? { label: yAxisLabel, text: METRIC_HELP[yMetric] } : null;
    const activeMetricHelpLines = activeMetricHelp ? wrapSvgText(activeMetricHelp.text, isLargeScatter ? 48 : 42) : [];
    const yAxisCenter = (plotTop + plotBottom) / 2;
    const yAxisInfoOffset = Math.max(52, (yAxisLabel.length * 4.6) + 18);
    const yAxisInfoX = 27;
    const yAxisInfoY = yAxisCenter - yAxisInfoOffset;
    const yAxisTooltipWidth = isLargeScatter ? 330 : 292;
    const yAxisTooltipHeight = Math.max(46, 22 + (activeMetricHelpLines.length * 16));
    const yAxisTooltipX = 18;
    const yAxisTooltipY = Math.max(-yAxisInfoY + 12, -Math.round(yAxisTooltipHeight / 2));

    if (isCompactScatter) {
      return (
        <div className="analytics-visual analytics-visual-scatter-list">
        {rankedCategories.map((category) => (
          <div
            key={category.id}
            className={`analytics-visual-scatter-list-row${onSelectCategory ? ' clickable' : ''}`}
            onClick={onSelectCategory ? () => onSelectCategory(category.id) : undefined}
          >
            <div className="analytics-visual-scatter-list-name">
              <span className={`analytics-visual-legend-swatch ${category.seriesTone || category.tone || 'accent'}`} />
              <div className="analytics-visual-scatter-list-copy">
                <span className="analytics-visual-label">{uiText(category.label)}</span>
                {model.activeGraph === ANALYTICS_GRAPH_TYPES.BUBBLE_CHART && category.riskTone ? (
                  <span className={`analytics-visual-risk-chip ${category.riskTone}`}>{getRiskLabel(category.riskTone)}</span>
                ) : null}
              </div>
              </div>
              <div className="analytics-visual-scatter-list-metrics">
                <strong>{formatMetric(category.metrics[yMetric], metricMap[yMetric]?.unit)}</strong>
                {metricMap[xMetric] && xMetric !== yMetric ? (
                  <span>{formatMetric(category.metrics[xMetric], metricMap[xMetric]?.unit)}</span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className={`analytics-visual analytics-visual-scatter ${isLargeScatter ? 'analytics-visual-scatter-large' : ''}`}>
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
            {rootDataset.selectors.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                onClick={() => setSelectedViewId(option.id)}
              >
                {uiText(option.label)}
              </button>
            ))}
          </div>
        ) : null}
        <div className={`analytics-visual-scatter-layout ${isLargeScatter ? 'large' : 'inline'}`}>
          <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="analytics-visual-trend-svg" role="img" aria-label={uiText("Comparison scatter")}>
            {showQuadrants ? (
              <>
                <rect x={plotLeft} y={plotTop} width={xAveragePosition - plotLeft} height={yAveragePosition - plotTop} className="analytics-visual-scatter-quadrant success" />
                <rect x={xAveragePosition} y={plotTop} width={plotRight - xAveragePosition} height={yAveragePosition - plotTop} className="analytics-visual-scatter-quadrant warning" />
                <rect x={plotLeft} y={yAveragePosition} width={xAveragePosition - plotLeft} height={plotBottom - yAveragePosition} className="analytics-visual-scatter-quadrant accent" />
                <rect x={xAveragePosition} y={yAveragePosition} width={plotRight - xAveragePosition} height={plotBottom - yAveragePosition} className="analytics-visual-scatter-quadrant danger" />
              </>
            ) : null}
            <line x1={plotLeft} y1={plotBottom} x2={plotRight} y2={plotBottom} className="analytics-visual-axis" />
            <line x1={plotLeft} y1={plotTop} x2={plotLeft} y2={plotBottom} className="analytics-visual-axis" />
            <line x1={xAveragePosition} y1={plotTop} x2={xAveragePosition} y2={plotBottom} className="analytics-visual-axis-guide" />
            <line x1={plotLeft} y1={yAveragePosition} x2={plotRight} y2={yAveragePosition} className="analytics-visual-axis-guide" />
            {categories.map((category) => {
              const x = plotLeft + ((Number(category.metrics[xMetric]) || 0) / xMax) * plotWidth;
              const y = plotBottom - ((Number(category.metrics[yMetric]) || 0) / yMax) * plotHeight;
              const size = model.activeGraph === ANALYTICS_GRAPH_TYPES.BUBBLE_CHART
                ? (isLargeScatter ? 12 : 10) + (((Number(category.metrics[bubbleMetric]) || 0) / bubbleMax) * (isLargeScatter ? 18 : 12))
                : (isLargeScatter ? 10 : 8);
              const fillTone = category.seriesTone || category.tone || 'accent';
              return (
                <g
                  key={category.id}
                  onClick={onSelectCategory ? () => onSelectCategory(category.id) : undefined}
                  className={onSelectCategory ? 'analytics-visual-scatter-point-clickable' : ''}
                >
                  <circle cx={x} cy={y} r={size} className={`analytics-visual-dot ${fillTone}`} fillOpacity={model.activeGraph === ANALYTICS_GRAPH_TYPES.BUBBLE_CHART ? 0.72 : 0.9} />
                </g>
              );
            })}
            {showQuadrants && showQuadrantLabels ? (
              <>
                <text x={plotLeft + 18} y={plotTop + 22} className="analytics-visual-scatter-quadrant-label">{quadrantLabels.topLeft}</text>
                <text x={plotRight - 18} y={plotTop + 22} textAnchor="end" className="analytics-visual-scatter-quadrant-label">{quadrantLabels.topRight}</text>
                <text x={plotLeft + 18} y={plotBottom - 16} className="analytics-visual-scatter-quadrant-label">{quadrantLabels.bottomLeft}</text>
                <text x={plotRight - 18} y={plotBottom - 16} textAnchor="end" className="analytics-visual-scatter-quadrant-label">{quadrantLabels.bottomRight}</text>
              </>
            ) : null}
            <text x={plotLeft} y={chartHeight - 38} textAnchor="start" className="analytics-visual-scatter-axis-value">
              {formatMetric(0, metricMap[xMetric]?.unit)}
            </text>
            <text x={xAveragePosition} y={chartHeight - 38} textAnchor="middle" className="analytics-visual-scatter-axis-value">
              {formatMetric(xMidValue, metricMap[xMetric]?.unit)}
            </text>
            <text x={plotRight} y={chartHeight - 38} textAnchor="end" className="analytics-visual-scatter-axis-value">
              {formatMetric(xMax, metricMap[xMetric]?.unit)}
            </text>
            <text x={plotLeft - 14} y={plotBottom} textAnchor="end" className="analytics-visual-scatter-axis-value">
              {formatMetric(0, metricMap[yMetric]?.unit)}
            </text>
            <text x={plotLeft - 14} y={yAveragePosition + 5} textAnchor="end" className="analytics-visual-scatter-axis-value">
              {formatMetric(yMidValue, metricMap[yMetric]?.unit)}
            </text>
            <text x={plotLeft - 14} y={plotTop + 5} textAnchor="end" className="analytics-visual-scatter-axis-value">
              {formatMetric(yMax, metricMap[yMetric]?.unit)}
            </text>
            <text x={(plotLeft + plotRight) / 2} y={chartHeight - 18} textAnchor="middle" className="analytics-visual-axis-label">
              {metricMap[xMetric]?.label || xMetric}
            </text>
            <g transform={`translate(28 ${(plotTop + plotBottom) / 2}) rotate(-90)`}>
              <text textAnchor="middle" className="analytics-visual-axis-label">
                {yAxisLabel}
              </text>
            </g>
            {activeMetricHelp ? (
              <g className="analytics-visual-axis-info-root" transform={`translate(${yAxisInfoX} ${yAxisInfoY})`} tabIndex="0">
                <circle r="8" />
                <text textAnchor="middle" dominantBaseline="central">i</text>
                <g className="analytics-visual-axis-info-tooltip" transform={`translate(${yAxisTooltipX} ${yAxisTooltipY})`}>
                  <rect width={yAxisTooltipWidth} height={yAxisTooltipHeight} rx="10" />
                  <text x="12" y="19">
                    {activeMetricHelpLines.map((line, index) => (
                      <tspan key={`${line}-${index}`} x="12" dy={index === 0 ? 0 : 16}>{line}</tspan>
                    ))}
                  </text>
                </g>
              </g>
            ) : null}
          </svg>

          {isLargeScatter ? (
            <div className="analytics-visual-scatter-side-list">
              {rankedCategories.map((category) => (
                <div
                  key={category.id}
                  className={`analytics-visual-scatter-side-item${onSelectCategory ? ' clickable' : ''}`}
                  onClick={onSelectCategory ? () => onSelectCategory(category.id) : undefined}
                >
                  <span className={`analytics-visual-legend-swatch ${category.seriesTone || category.tone || 'accent'}`} />
                  <div className="analytics-visual-scatter-side-copy">
                    <strong>{uiText(category.label)}</strong>
                    <span>
                      {formatMetric(category.metrics[xMetric], metricMap[xMetric]?.unit)} • {formatMetric(category.metrics[yMetric], metricMap[yMetric]?.unit)}
                    </span>
                    {model.activeGraph === ANALYTICS_GRAPH_TYPES.BUBBLE_CHART && category.riskTone ? (
                      <span className={`analytics-visual-risk-chip ${category.riskTone}`}>{getRiskLabel(category.riskTone)}</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : isMediumScatter ? (
            <div className="analytics-visual-scatter-bottom-list">
              {rankedCategories.map((category) => (
                <div
                  key={category.id}
                  className={`analytics-visual-scatter-bottom-item${onSelectCategory ? ' clickable' : ''}`}
                  onClick={onSelectCategory ? () => onSelectCategory(category.id) : undefined}
                >
                  <span className={`analytics-visual-legend-swatch ${category.seriesTone || category.tone || 'accent'}`} />
                  <div className="analytics-visual-scatter-bottom-copy">
                    <strong>{uiText(category.label)}</strong>
                    <span>
                      {uiText(metricMap[xMetric]?.label)}: {formatMetric(category.metrics[xMetric], metricMap[xMetric]?.unit)} • {uiText(metricMap[yMetric]?.label)}: {formatMetric(category.metrics[yMetric], metricMap[yMetric]?.unit)}
                    </span>
                    {model.activeGraph === ANALYTICS_GRAPH_TYPES.BUBBLE_CHART && category.riskTone ? (
                      <span className={`analytics-visual-risk-chip ${category.riskTone}`}>{getRiskLabel(category.riskTone)}</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (model.activeGraph === ANALYTICS_GRAPH_TYPES.BULLET_CHART) {
    return (
      <div className="analytics-visual analytics-visual-bullets">
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
            {rootDataset.selectors.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                onClick={() => setSelectedViewId(option.id)}
              >
                {uiText(option.label)}
              </button>
            ))}
          </div>
        ) : null}
        {categories.map((category) => {
          const actual = Number(category.metrics.actual ?? category.metrics[primary.id]) || 0;
          const target = Number(category.metrics.target ?? category.metrics[tertiary?.id] ?? actual) || 0;
          return (
            <div key={category.id} className="analytics-visual-row">
              <div className="analytics-visual-row-head">
                <span className="analytics-visual-label">{uiText(category.label)}</span>
                <strong className="analytics-visual-number">{formatMetric(actual, primary.unit)}</strong>
              </div>
              <div className="analytics-visual-bullet-track">
                <div className={`analytics-visual-fill ${category.tone || 'accent'}`} style={{ width: `${Math.max(10, (actual / Math.max(target, actual, 1)) * 100)}%` }} />
                <span className="analytics-visual-bullet-marker" style={{ left: `${Math.min(98, (target / Math.max(target, actual, 1)) * 100)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (model.activeGraph === ANALYTICS_GRAPH_TYPES.TABLE) {
    const tableColumns = (activeDataset?.tableColumns?.length ? activeDataset.tableColumns : null)
      || (graphConfig.tableColumns?.length ? graphConfig.tableColumns : null)
      || metrics.map((metric) => ({
        id: metric.id,
        label: metric.label,
        unit: metric.unit,
        format: metric.unit === '%' ? 'percent' : metric.unit === 'days' || metric.unit === 'أيام' ? 'days' : metric.unit === 'score' ? 'score' : 'count'
      }));
    const tableGridTemplateColumns = `minmax(180px, 1.25fr) repeat(${tableColumns.length}, minmax(0, 0.85fr))`;

    return (
      <div className="analytics-visual analytics-visual-table">
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
            {rootDataset.selectors.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                onClick={() => setSelectedViewId(option.id)}
              >
                {uiText(option.label)}
              </button>
            ))}
          </div>
        ) : null}
        <div className="analytics-visual-table-grid">
          <div className="analytics-visual-table-head" style={{ gridTemplateColumns: tableGridTemplateColumns }}>
            <span className="analytics-visual-table-head-label" />
            {tableColumns.map((column) => (
              <span key={column.id} className="analytics-visual-table-head-cell">{uiText(column.label)}</span>
            ))}
          </div>
          {categories.map((category) => (
            <div
              key={category.id}
              className={`analytics-visual-table-row analytics-visual-table-row-grid${onSelectCategory ? ' clickable' : ''}`}
              onClick={onSelectCategory ? () => onSelectCategory(category.id) : undefined}
              onKeyDown={onSelectCategory ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectCategory(category.id);
                }
              } : undefined}
              role={onSelectCategory ? 'button' : undefined}
              tabIndex={onSelectCategory ? 0 : undefined}
              style={{ gridTemplateColumns: tableGridTemplateColumns }}
            >
              <span className="analytics-visual-table-row-label">{uiText(category.label)}</span>
              {tableColumns.map((column) => (
                <span key={column.id} className="analytics-visual-table-cell">
                  {formatTableValue(category.metrics[column.id], column)}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (model.activeGraph === ANALYTICS_GRAPH_TYPES.SOURCE_RANKINGS) {
    const valueMetric = metricMap.value || metrics[0];
    const growthMetric = metricMap.momGrowth || metrics[1];
    const maxValue = Math.max(...categories.map((category) => Number(category.metrics[valueMetric?.id]) || 0), 1);

    return (
      <div className="analytics-visual analytics-visual-source-rankings">
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-source-picker" role="tablist" aria-label={uiText("Source ranking selector")}>
            {rootDataset.selectors.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                onClick={() => setSelectedViewId(option.id)}
              >
                {uiText(option.label)}
              </button>
            ))}
          </div>
        ) : null}
        <div className="analytics-visual-source-list">
          {categories.map((category, index) => {
            const value = Number(category.metrics[valueMetric.id]) || 0;
            const growth = Number(category.metrics[growthMetric?.id]) || 0;
            return (
              <div key={category.id} className="analytics-visual-source-row">
                <div className="analytics-visual-row-head">
                  <span className="analytics-visual-label">{index + 1}. {uiText(category.label)}</span>
                  <div className="analytics-visual-row-metrics">
                    <strong className="analytics-visual-number">{formatMetric(value, valueMetric.unit)}</strong>
                    <span className={`analytics-visual-sub ${growth >= 0 ? 'success' : 'danger'}`}>
                      {growth >= 0 ? '+' : ''}{formatMetric(growth, growthMetric?.unit)}
                    </span>
                  </div>
                </div>
                <div className="analytics-visual-track slim analytics-visual-source-track">
                  <div
                    className={`analytics-visual-fill ${category.tone || 'accent'}`}
                    style={{ width: `${Math.max(10, (value / maxValue) * 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (model.activeGraph === ANALYTICS_GRAPH_TYPES.GROUPED_BAR || model.activeGraph === ANALYTICS_GRAPH_TYPES.STACKED_BAR) {
    if (graphConfig.presentation === 'duration_breakdown') {
      const durationMetric = metricMap[graphConfig.primaryMetricId] || primary;
      const latestMetric = metricMap.latestMonth;
      const shareMetric = metricMap.share;
      const maxDuration = Math.max(
        ...categories.map((category) => Number(category.metrics[durationMetric.id]) || 0),
        1
      );

      return (
      <div className="analytics-visual analytics-visual-grouped analytics-visual-duration-breakdown">
          {rootDataset?.selectors?.length ? (
            <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
              {rootDataset.selectors.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                  onClick={() => setSelectedViewId(option.id)}
                >
                  {uiText(option.label)}
                </button>
              ))}
            </div>
          ) : null}
          {categories.map((category) => {
            const averageDays = Number(category.metrics[durationMetric.id]) || 0;
            const latestMonth = latestMetric ? Number(category.metrics[latestMetric.id]) || 0 : 0;
            const share = shareMetric ? Number(category.metrics[shareMetric.id]) || 0 : 0;
            const fillWidth = (averageDays / maxDuration) * 100;
            const markerPosition = (latestMonth / maxDuration) * 100;

            return (
              <div key={category.id} className="analytics-visual-grouped-row analytics-visual-duration-row">
                <div className="analytics-visual-row-head">
                  <span className="analytics-visual-label">{uiText(category.label)}</span>
                  <div className="analytics-visual-row-metrics analytics-visual-duration-metrics">
                    <strong className="analytics-visual-number">{formatMetric(averageDays, durationMetric.unit)}</strong>
                    {latestMetric ? (
                      <span className="analytics-visual-sub">
                        {uiText(latestMetric.label)} {formatMetric(latestMonth, latestMetric.unit)}
                      </span>
                    ) : null}
                    {shareMetric ? (
                      <span className="analytics-visual-sub">
                        {uiText(shareMetric.label)} {formatMetric(share, shareMetric.unit)}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="analytics-visual-track slim analytics-visual-duration-track">
                  <div
                    className={`analytics-visual-fill ${category.tone || 'accent'}`}
                    style={{ width: `${Math.max(0, Math.min(fillWidth, 100))}%` }}
                  />
                  {latestMetric ? (
                    <span
                      className="analytics-visual-duration-marker"
                      style={{ left: `${Math.max(0, Math.min(markerPosition, 100))}%` }}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (graphConfig.presentation === 'overview_metric_rows') {
      const chartMetrics = barMetricIds
        .map((metricId) => metricMap[metricId])
        .filter(Boolean);

      return (
        <div className="analytics-visual analytics-visual-grouped analytics-visual-overview-metric-rows">
          {rootDataset?.selectors?.length ? (
            <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
              {rootDataset.selectors.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                  onClick={() => setSelectedViewId(option.id)}
                >
                  {uiText(option.label)}
                </button>
              ))}
            </div>
          ) : null}
          {categories.map((category) => (
            <div key={category.id} className="analytics-visual-grouped-row analytics-visual-overview-metric-group">
              <div className="analytics-visual-row-head">
                <span className="analytics-visual-label">{uiText(category.label)}</span>
              </div>

              <div className="analytics-visual-overview-metric-list">
                {chartMetrics.map((metric, index) => {
                  const value = Number(category.metrics[metric.id]) || 0;
                  return (
                    <div key={`${category.id}-${metric.id}`} className="analytics-visual-row analytics-visual-overview-metric-row">
                      <div className="analytics-visual-row-head">
                        <span className="analytics-visual-sub analytics-visual-overview-metric-label">{uiText(metric.label)}</span>
                        <strong className="analytics-visual-number analytics-visual-overview-metric-value">
                          {formatMetric(value, metric.unit)}
                        </strong>
                      </div>
                      <div className="analytics-visual-track slim analytics-visual-overview-metric-track">
                        <div
                          className={`analytics-visual-fill ${index === 0 ? 'series-100' : index === 1 ? 'series-70' : 'series-45'}`}
                          style={{ width: `${Math.max(8, Math.min(value, 100))}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (graphConfig.presentation === 'benchmark_ranked_rows') {
      const benchmarkMetric = contextMetricIds.map((metricId) => metricMap[metricId]).find((metric) => metric?.id === 'teamAverage') || contextMetricIds.map((metricId) => metricMap[metricId]).find(Boolean);
      const maxPrimary = Math.max(...categories.map((category) => Number(category.metrics[displayMetric.id]) || 0), 1);
      const benchmarkValue = benchmarkMetric ? averageMetric(categories, benchmarkMetric.id) : 0;
      const benchmarkWidth = benchmarkMetric ? (benchmarkValue / maxPrimary) * 100 : 0;

      return (
        <div className="analytics-visual analytics-visual-grouped analytics-visual-benchmark-rows">
          {categories.map((category) => {
            const value = Number(category.metrics[displayMetric.id]) || 0;
            const width = (value / maxPrimary) * 100;
            return (
              <div key={category.id} className="analytics-visual-grouped-row analytics-visual-benchmark-row">
                <div className="analytics-visual-row-head">
                  <span className="analytics-visual-label">{uiText(category.label)}</span>
                  <div className="analytics-visual-row-metrics">
                    <strong className="analytics-visual-number">{formatMetric(value, displayMetric.unit)}</strong>
                    {contextMetricIds
                      .map((metricId) => metricMap[metricId])
                      .filter(Boolean)
                      .map((metric) => (
                        <span key={`${category.id}-${metric.id}`} className="analytics-visual-sub">
                          {uiText(metric.label)} {formatMetric(category.metrics[metric.id], metric.unit)}
                        </span>
                      ))}
                  </div>
                </div>
                <div className="analytics-visual-track slim analytics-visual-benchmark-track">
                  <div
                    className={`analytics-visual-fill ${category.tone || 'accent'}`}
                    style={{ width: `${Math.max(8, Math.min(width, 100))}%` }}
                  />
                  {benchmarkMetric ? (
                    <span
                      className="analytics-visual-benchmark-marker"
                      style={{ left: `${Math.max(2, Math.min(benchmarkWidth, 98))}%` }}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="analytics-visual analytics-visual-grouped">
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
            {rootDataset.selectors.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                onClick={() => setSelectedViewId(option.id)}
              >
                {uiText(option.label)}
              </button>
            ))}
          </div>
        ) : null}
        {categories.map((category) => {
          const displayValue = Number(category.metrics[displayMetric.id]) || 0;
          const barMetrics = barMetricIds
            .map((metricId) => metricMap[metricId])
            .filter(Boolean);
          const first = Number(category.metrics[barMetrics[0]?.id]) || 0;
          const second = barMetrics[1] ? Number(category.metrics[barMetrics[1].id]) || 0 : 0;
          const third = barMetrics[2] ? Number(category.metrics[barMetrics[2].id]) || 0 : 0;
          const total = model.activeGraph === ANALYTICS_GRAPH_TYPES.STACKED_BAR
            ? Math.max(first + second + third, 1)
            : Math.max(...barMetrics.map((metric) => Math.max(...categories.map((item) => Number(item.metrics[metric.id]) || 0), 0)), 1);
          return (
            <div
              key={category.id}
              className={`analytics-visual-grouped-row${onSelectCategory ? ' clickable' : ''}`}
              onClick={onSelectCategory ? () => onSelectCategory(category.id) : undefined}
              onKeyDown={onSelectCategory ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectCategory(category.id);
                }
              } : undefined}
              role={onSelectCategory ? 'button' : undefined}
              tabIndex={onSelectCategory ? 0 : undefined}
            >
              <div className="analytics-visual-row-head">
                <span className="analytics-visual-label">{uiText(category.label)}</span>
                <div className="analytics-visual-row-metrics">
                  <strong className="analytics-visual-number">{formatMetric(displayValue, displayMetric.unit)}</strong>
                  {contextMetricIds
                    .map((metricId) => metricMap[metricId])
                    .filter(Boolean)
                    .map((metric) => (
                      <span key={`${category.id}-${metric.id}`} className="analytics-visual-sub">
                        {uiText(metric.label)} {formatMetric(category.metrics[metric.id], metric.unit)}
                      </span>
                    ))}
                </div>
              </div>
              {model.activeGraph === ANALYTICS_GRAPH_TYPES.GROUPED_BAR ? (
                <div className="analytics-visual-grouped-comparison">
                  {barMetrics.map((metric, index) => {
                    const value = Number(category.metrics[metric.id]) || 0;
                    const width = (value / total) * 100;
                    const toneClass = index === 0 ? (category.tone || 'accent') : index === 1 ? 'series-70' : 'series-45';
                    return (
                      <div key={`${category.id}-${metric.id}`} className="analytics-visual-grouped-comparison-row">
                        <div className="analytics-visual-grouped-comparison-meta">
                          <span className="analytics-visual-sub analytics-visual-grouped-comparison-label">{uiText(metric.label)}</span>
                          <strong className="analytics-visual-number analytics-visual-grouped-comparison-value">
                            {formatMetric(value, metric.unit)}
                          </strong>
                        </div>
                        <div className="analytics-visual-grouped-comparison-track">
                          <div className={`analytics-visual-grouped-comparison-fill ${toneClass}`} style={{ width: `${Math.max(8, Math.min(width, 100))}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="analytics-visual-grouped-bars">
                  <div className="analytics-visual-grouped-bar">
                    <div className={`analytics-visual-fill ${category.tone || 'accent'}`} style={{ width: `${Math.max(8, (first / total) * 100)}%` }} />
                  </div>
                  {barMetrics[1] ? (
                    <div className="analytics-visual-grouped-bar secondary">
                      <div className="analytics-visual-fill series-70" style={{ width: `${Math.max(8, (second / (model.activeGraph === ANALYTICS_GRAPH_TYPES.STACKED_BAR ? total : secondaryMax)) * 100)}%` }} />
                    </div>
                  ) : null}
                  {barMetrics[2] && model.activeGraph === ANALYTICS_GRAPH_TYPES.STACKED_BAR ? (
                    <div className="analytics-visual-grouped-bar secondary">
                      <div className="analytics-visual-fill series-45" style={{ width: `${Math.max(8, (third / total) * 100)}%` }} />
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="analytics-visual analytics-visual-rows">
      {rootDataset?.selectors?.length ? (
        <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
          {rootDataset.selectors.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
              onClick={() => setSelectedViewId(option.id)}
            >
              {uiText(option.label)}
            </button>
          ))}
        </div>
      ) : null}
      {categories.map((category) => {
        const actual = Number(category.metrics.actual ?? category.metrics[displayMetric.id]) || 0;
        const target = Number(category.metrics.target ?? category.metrics[primary.id] ?? actual) || 0;
        const progress = Number(category.metrics.percentComplete ?? ((actual / Math.max(target, actual, 1)) * 100)) || 0;
        return (
          <div
            key={category.id}
            className={`analytics-visual-row${onSelectCategory ? ' clickable' : ''}`}
            onClick={onSelectCategory ? () => onSelectCategory(category.id) : undefined}
            onKeyDown={onSelectCategory ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelectCategory(category.id);
              }
            } : undefined}
            role={onSelectCategory ? 'button' : undefined}
            tabIndex={onSelectCategory ? 0 : undefined}
          >
            <div className="analytics-visual-row-head">
              <span className="analytics-visual-label">{uiText(category.label)}</span>
              <div className="analytics-visual-row-metrics">
                <strong className="analytics-visual-number">{formatMetric(actual, displayMetric.unit)}</strong>
                {contextMetricIds
                  .map((metricId) => metricMap[metricId])
                  .filter(Boolean)
                  .map((metric) => (
                    <span key={`${category.id}-${metric.id}`} className="analytics-visual-sub">
                      {uiText(metric.label)} {formatMetric(category.metrics[metric.id], metric.unit)}
                    </span>
                  ))}
              </div>
            </div>
            <div className="analytics-visual-track slim">
              <div className={`analytics-visual-fill ${category.tone || 'accent'}`} style={{ width: `${Math.max(8, Math.min(progress, 100))}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ComparisonVisual;
